<template>
    <modal id="addUserModal" v-model:show="displayModal" class="modal fade" :show-close="true">
        <template #header>
            <h6 v-if="selectedUser" class="modal-title">Edit User : {{ selectedUser.name }}</h6>
            <h6 v-else class="modal-title">Add User</h6>
        </template>

        <!--User form data -->
        <el-form 
            ref="formRef"
            class="form-container"
            :model="userform" 
            :rules="formRules" 
            label-position="left" 
            label-width="auto" 
            status-icon
            >

            <el-form-item label="User Id" prop="user_id">
                <el-input v-model="userform.user_id" :disabled="!!selectedUser"  placeholder="eg.G123456" />
            </el-form-item>

            <el-form-item v-if="!selectedUser" label="Password" prop="password">
                <el-input v-model="userform.password" type="password" show-password />
            </el-form-item>

            <RoleDropdownV2 
                id="ddRole" 
                :selected="userform.role_id" 
                prop="role_id" 
                :is-create-mode="isCreateMode" 
                :selected-user="selectedUser"
                @selected-role-id="(selectedValue) => (userform.role_id = selectedValue)" />

            <el-form-item label="Full Name" prop="name">
                <el-input v-model="userform.name" placeholder="Enter your name" />
            </el-form-item>

            <PlatoonDropdownV2 
                id="ddPlatoon" 
                :allow-all="false" 
                :selected="userform.platoon_id" @selected-platoon-id="
                (selectedValue) => (userform.platoon_id = selectedValue)" 
            />

            <SectionDropdownV2 
                id="ddSection" 
                :selected="userform.section_id" 
                :allow-all="false" 
                @selected-section-id="(selectedValue) => (userform.section_id = selectedValue)" 
            />

            <el-form-item label="Gender" prop="gender">
                <el-radio-group v-model="userform.gender">
                    <el-radio label="Male" value="M" />
                    <el-radio label="Female" value="F" />
                </el-radio-group>
            </el-form-item>

            <el-form-item label="Date Of Birth" prop="date_of_birth">
                <el-date-picker 
                    v-model="userform.date_of_birth" 
                    type="date" 
                    aria-label="Select DOB"
                    placeholder="Select DOB" 
                    style="width: 100%" />
            </el-form-item>

            <el-form-item>
                <el-col :span="11">
                    <el-form-item label="Height" prop="height_cm">
                        <el-input v-model="userform.height_cm" type="number" placeholder="Enter Height" min="30" />
                    </el-form-item>
                </el-col>
                <el-col :span="2" />
                <el-col :span="11">
                    <el-form-item label="Weight" prop="weight_kg">
                        <el-input v-model="userform.weight_kg" type="number" placeholder="Enter Weight" min="15" />
                    </el-form-item>
                </el-col>
            </el-form-item>

            <hr class="horizontal dark device-separator" />
            <h6 class="mt-3">Devices</h6>
            <div class="text-xs text-light color-secondary mt-2 mb-3">
                Assign one or more devices to the user. Not mandatory for
                Android App users.
            </div>
            <el-form-item v-for="(device, idx) in userform.devices" :key="idx" class="row">
                <el-col :span="11">
                    <el-form-item label="Serial (SN)" prop="serialNumber">
                        <el-input v-model="device.serial" placeholder="eg. KSTXXXX..." />
                    </el-form-item>
                </el-col>
                <el-col :span="2" />
                <el-col :span="11">
                    <el-form-item label="MAC Address" prop="macAddress">
                        <el-input v-model="device.address" placeholder="eg. 00:00:00:00:00:00" />
                    </el-form-item>
                </el-col>
            </el-form-item>
        </el-form>

        <div class="d-flex justify-content-between">
            <button 
                v-if="!isCreateMode && selectedUser && selectedUser.is_active" 
                type="button"
                title="Upon deactivation, user will no longer be able to log in to the system."
                class="btn btn-link text-danger" 
                @click="deactivateAccount()">
                Deactivate
            </button>
            <button 
                v-if="!isCreateMode && selectedUser && !selectedUser.is_active" 
                type="button"
                title="Upon activation, user will be able to log in to the system."
                class="btn btn-link text-success" 
                @click="activateAccount()">
                Activate
            </button>

            <button 
                v-if="!isCreateMode && selectedUser" 
                type="button"
                title="Upon token revocation, user will no longer be able to send fitness data to the system. User may re-authenticate to resume access."
                class="btn btn-link text-light float-start" 
                @click="revokeToken()">
                Revoke Token
            </button>

            <button v-if="isCreateMode" type="button" class="btn btn-light float-end" @click="resetForm">Reset</button>
            <button :disabled="userCreationInProgress" type="button" class="btn btn-primary" @click="submitForm">Save</button>
        </div>
    </modal>
</template>

<script>
import Modal from "@/shared/Modal.vue";
import DateUtils from "@/helpers/dateUtils";
import PlatoonDropdownV2 from "../../components/PlatoonDropdownV2.vue";
import SectionDropdownV2 from "../../components/SectionDropdownV2.vue";
import RoleDropdownV2 from "../../components/RoleDropdownV2.vue";

export default {
    name: 'UserRegistration',
    components: {
        Modal,
        PlatoonDropdownV2,
        SectionDropdownV2,
        RoleDropdownV2,
    },
    props: {
        selectedUser: {
            type: Object,
            required: false,
            default: null,
        },
        isCreateMode: {
            type: Boolean,
            default: false,
        },
        formRules: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            userform: {
                user_id: '',
                password: '',
                role_id: -1,
                name: '',
                platoon_id: -1,
                section_id: -1,
                gender: 'M',
                date_of_birth: '',
                height_cm: 0,
                weight_kg: 0,
                devices: [
                    { serial: "", address: "", },
                    { serial: "", address: "", },
                ],
            },
            addingUser: false,
            displayModal: false,
            userCreationInProgress: false,
        };
    },
    mounted() {
        this.displayModal = true
        if (!this.isCreateMode) {
            this.populateUserForm();
        } 
    },
    methods: {
        populateUserForm() {
            this.userform.user_id = this.selectedUser.user_id;
            this.userform.role_id = this.selectedUser.role_id;
            this.userform.name = this.selectedUser.name;
            this.userform.platoon_id = this.selectedUser.platoon_id;
            this.userform.section_id = this.selectedUser.section_id;
            this.userform.date_of_birth = DateUtils.formatDateToBucketDate(this.selectedUser.date_of_birth);
            this.userform.gender = this.selectedUser.gender == 'M' ? 'Male' : 'Female';
            this.userform.height_cm = this.selectedUser.height_cm;
            this.userform.weight_kg = this.selectedUser.weight_kg;
            this.userform.devices = this.selectedUser.assigned_devices;
            this.userform.devices[0].serial = this.selectedUser.assigned_devices[0].serial;
            this.userform.devices[0].address = this.selectedUser.assigned_devices[0].address;
            this.userform.devices[1].serial = this.selectedUser.assigned_devices[1].serial;
            this.userform.devices[1].address = this.selectedUser.assigned_devices[1].address;
        },
        submitForm() {
            this.$refs.formRef.validate((valid) => {
                if (valid) {
                    this.userCreationInProgress = true;
                    if (this.selectedUser) {
                        //TODO save edited user data
                        this.editUser();
                    } else {
                        this.$parent.addingUser = true;
                        this.addUser();
                    }
                }
            });
        },
        resetForm() {
            this.$refs.formRef.resetFields();
            this.userCreationInProgress = false;
        },
        addUser() {
            this.$parent.addingUser = true;
            let userRequest = { ... this.userform };
            userRequest.gender = userRequest.gender == 'Male' ? 'M' : 'F';
            userRequest.date_of_birth = DateUtils.formatDateToBucketDate(userRequest.date_of_birth);
            userRequest.password = {
                fieldType: "password",
                value: this.userform.password,
            };
            userRequest.active = true;
            this.userCreationInProgress = true;
            this.$store.dispatch("user/add", userRequest).then((res) => {
                this.resetForm();
                this.$snackbar.add({
                    type: "success",
                    text: `Account added for ${res.data.name}`,
                });
                this.resetParentData();
            });
        },
        editUser() {
            let userRequest = { ... this.userform };
            userRequest.gender = userRequest.gender == 'Male' ? 'M' : 'F';
            userRequest.date_of_birth = DateUtils.formatDateToBucketDate(userRequest.date_of_birth);
            userRequest.udid = this.selectedUser.udid;
            this.userCreationInProgress = true;
            this.$store.dispatch("user/edit", userRequest).then((res) => {
                this.$snackbar.add({
                    type: "success",
                    text: `Account updated for ${res.data.name}`,
                });
                this.resetParentData();
                this.$parent.clear();
                this.userCreationInProgress = false;
            }).catch((error) => {
                this.userCreationInProgress = false;
                this.$snackbar.add({
                    type: "error",
                    text: `Error updating account: ${error.message}`,
                });
            });
        },
        closeModal() {
            this.resetParentData();
            this.$parent.addingUser = false;
        },
        deactivateAccount() {
            this.$store
                .dispatch("user/deactivate", this.selectedUser.udid)
                .then(() => {
                    this.$snackbar.add({
                        type: "success",
                        text: `${this.selectedUser.name}'s account is now deactivated`,
                    });
                    this.resetParentData();
                });
        },
        activateAccount() {
            this.$store.dispatch("user/activate", this.selectedUser.udid).then(() => {
                this.$snackbar.add({
                    type: "success",
                    text: `${this.selectedUser.name}'s account is now activated`,
                });
                this.resetParentData();
            });
        },
        revokeToken() {
            this.$store
                .dispatch("user/revokeToken", this.selectedUser.udid)
                .then(() => {
                    this.$snackbar.add({
                        type: "success",
                        text: `${this.selectedUser.name}'s token is now revoked.`,
                    });
                    this.resetParentData();
                });
        },
        resetParentData() {
            this.$parent.getUserList();
            if(this.selectedUser) {
                this.$parent.animateRow(this.selectedUser.idx);
            }
            this.$parent.selectedUser = null;
            this.$parent.addingUser = false;
            this.$parent.resetModals();
            this.displayModal = false;
        }
    }
};
</script>

<style scoped>
.form-container {
    max-width: 40rem;
    margin: auto;
    padding: 1rem;
    background-color: #283335;
    color: white;
    border-radius: 0.5rem;
}

.device-separator {
    margin-top: 2rem;
    height: 0.2rem;
}
</style>